import React from "react";
import { TimeSlot } from "../../../types";

interface TimeSlotSelectorProps {
  timeSlots: TimeSlot[];
  selectedTimeSlot: TimeSlot | null;
  onTimeSlotSelect: (timeSlot: TimeSlot) => void;
  loading: boolean;
  error: string | null;
  label?: string;
}

export const TimeSlotSelector: React.FC<TimeSlotSelectorProps> = ({
  timeSlots,
  selectedTimeSlot,
  onTimeSlotSelect,
  loading,
  error,
  label = "Horário",
}) => {
  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-700">
        {label}
      </label>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-8 space-y-4">
          <div className="relative">
            <div className="animate-spin w-10 h-10 border-4 border-sky-200 border-t-sky-600 rounded-full"></div>
            <div className="absolute inset-0 animate-ping w-10 h-10 border-4 border-sky-300 rounded-full opacity-20"></div>
          </div>
          <p className="text-gray-600 font-medium text-sm">
            Carregando horários disponíveis...
          </p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      ) : timeSlots.length === 0 ? (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl text-gray-400">⏰</span>
          </div>
          <p className="text-sm text-gray-600">
            Não há horários disponíveis para esta data. Tente escolher outra
            data.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2 sm:gap-3 lg:gap-4">
          {timeSlots.map((slot, index) => (
            <button
              key={slot.startTime || index}
              type="button"
              onClick={() => onTimeSlotSelect(slot)}
              className={`group relative overflow-hidden p-2 sm:p-3 lg:p-4 rounded-lg sm:rounded-xl border-2 transition-all duration-300 transform hover:scale-105 ${
                selectedTimeSlot?.startTime === slot.startTime
                  ? "bg-gradient-to-br from-sky-500 to-sky-600 border-sky-400 text-white shadow-large ring-2 sm:ring-4 ring-sky-200"
                  : "bg-white border-gray-200 hover:border-sky-300 text-gray-700 shadow-soft hover:shadow-medium"
              }`}
              style={{ animationDelay: `${index * 50}ms` }}
              aria-label={`${slot.startTime} - Disponível`}
            >
              <div className="relative z-10 text-center space-y-0.5 sm:space-y-1">
                <div
                  className={`text-xs sm:text-base lg:text-lg font-bold ${
                    selectedTimeSlot?.startTime === slot.startTime
                      ? "text-white"
                      : "text-gray-800"
                  }`}
                >
                  {slot.startTime}
                </div>
              </div>

              {/* Hover effect background */}
              <div
                className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${
                  selectedTimeSlot?.startTime === slot.startTime
                    ? "bg-white/10"
                    : "bg-gradient-to-br from-sky-50 to-sky-100"
                }`}
              ></div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
